import { useState } from 'react';
import { useLang } from '../contexts/LangContext';
import styles from './AdminSettingsTab.module.css';

export default function AdminSettingsTab({ onToast }) {
  const { t } = useLang();
  
  // Payment Gateway Statuses
  const [stripeConnected, setStripeConnected] = useState(true);
  const [promptPayConnected, setPromptPayConnected] = useState(false);
  const [promptPayNumber, setPromptPayNumber] = useState('081-234-5678');
  
  // System Automations state
  const [automations, setAutomations] = useState([
    { id: 'welcome_email', name: 'Welcome Email on Signup', active: true, desc: 'Send onboarding email to newly registered users' },
    { id: 'line_notify', name: 'LINE Notify: New Signups', active: true, desc: 'Push alert message to administrator LINE channel on new user signups' },
    { id: 'trial_expiry', name: 'Trial Expiry Reminder', active: false, desc: 'Email users 3 days before their plan subscription trial expires' },
    { id: 'leaderboard_report', name: 'Weekly Leaderboard Report', active: true, desc: 'Generate and send community leaderboard summary every Sunday' }
  ]);

  // Utility execution loading states
  const [runningVacuum, setRunningVacuum] = useState(false);
  const [runningCacheClear, setRunningCacheClear] = useState(false);

  function toggleAutomation(id) {
    setAutomations(prev =>
      prev.map(item => item.id === id ? { ...item, active: !item.active } : item)
    );
    onToast(t('common.success') || 'Automation state updated');
  }

  async function executeVacuum() {
    setRunningVacuum(true);
    // Simulate database vacuum execution delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    setRunningVacuum(false);
    onToast('PostgreSQL VACUUM finished. Reclaimed index space and cleaned up inactive tuples.');
  }

  async function executeCacheClear() {
    setRunningCacheClear(true);
    // Simulate cache eviction delay
    await new Promise(resolve => setTimeout(resolve, 800));
    setRunningCacheClear(false);
    onToast('Serverpod Memory Cache invalidated. Cleaned up localized dictionary and session cache keys.');
  }

  return (
    <div className="flex-col">
      <div className="card">
        <div className="card-header">
          <div className="card-title">⚙️ System Configuration Settings</div>
        </div>
        <div className="card-body">
          {/* Payment Gateways Section */}
          <div className={styles.sectionTitle}>💳 Payment Gateway Integrations</div>
          <div className={styles.grid}>
            <div className={`${styles.gatewayCard} ${stripeConnected ? styles.connected : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Stripe International</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: stripeConnected ? 'var(--accent3)' : 'var(--text3)' }}>
                  {stripeConnected ? 'CONNECTED (LIVE)' : 'DISCONNECTED'}
                </span>
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Allows global card billing (Visa, Mastercard, Amex) and automatic recurring subscriptions.
              </div>
              <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
                <button 
                  className="btn btn-ghost btn-sm" 
                  style={{ flex: 1 }}
                  onClick={() => {
                    setStripeConnected(!stripeConnected);
                    onToast(stripeConnected ? 'Stripe disconnected' : 'Stripe connected in Live Mode');
                  }}
                >
                  {stripeConnected ? 'Disconnect' : 'Connect Stripe'}
                </button>
              </div>
            </div>

            <div className={`${styles.gatewayCard} ${promptPayConnected ? styles.connected : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>PromptPay Thailand QR</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: promptPayConnected ? 'var(--accent3)' : 'var(--text3)' }}>
                  {promptPayConnected ? 'CONNECTED' : 'INACTIVE'}
                </span>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Registered PromptPay ID (Mobile or Tax ID)</label>
                <input 
                  className="form-input" 
                  value={promptPayNumber} 
                  onChange={e => setPromptPayNumber(e.target.value)} 
                  style={{ padding: '6px 10px', fontSize: 12 }}
                />
              </div>
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => {
                  setPromptPayConnected(!promptPayConnected);
                  onToast(promptPayConnected ? 'PromptPay deactivated' : 'PromptPay QR payment active');
                }}
              >
                {promptPayConnected ? 'Deactivate' : 'Save & Enable QR Code'}
              </button>
            </div>
          </div>

          {/* System Automations Section */}
          <div className={styles.sectionTitle}>🤖 Webhooks & Background Actions</div>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', marginBottom: 24 }}>
            {automations.map(item => (
              <div key={item.id} className={styles.autoRow}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>{item.name}</div>
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{item.desc}</div>
                </div>
                <label className={styles.toggleSwitch}>
                  <input 
                    type="checkbox" 
                    checked={item.active} 
                    onChange={() => toggleAutomation(item.id)} 
                  />
                  <span className={styles.slider} />
                </label>
              </div>
            ))}
          </div>

          {/* Maintenance Utilities Section */}
          <div className={styles.sectionTitle}>🛠️ Database Maintenance Utilities</div>
          <div className={styles.grid}>
            <div className={styles.dbUtilityCard}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>PostgreSQL Garbage Collection</div>
                <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                  Executes database vacuum to optimize storage footprint and rebuild indexing matrices.
                </div>
              </div>
              <button 
                className="btn btn-ghost btn-sm" 
                style={{ flexShrink: 0 }}
                onClick={executeVacuum}
                disabled={runningVacuum}
              >
                {runningVacuum ? 'Running...' : 'Run VACUUM'}
              </button>
            </div>

            <div className={styles.dbUtilityCard}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Evict Session & Route Memory Cache</div>
                <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                  Clears localization keys and temporary route lookups from system runtime memory.
                </div>
              </div>
              <button 
                className="btn btn-ghost btn-sm" 
                style={{ flexShrink: 0 }}
                onClick={executeCacheClear}
                disabled={runningCacheClear}
              >
                {runningCacheClear ? 'Clearing...' : 'Clear Cache'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
